/**
 * Display formatting for alerts, shared by the user's own alert list and the
 * admin alert pages. Kept out of the components so the two surfaces can't
 * drift apart as condition types are added — there are 16 of them.
 */

/**
 * Short, human-readable rendering of an alert's condition (e.g. `> 200`).
 *
 * @param {object} condition   the alert's `condition` object
 * @param {Function} t         vue-i18n `t`
 * @param {Function} conditionLabel  `useAlertConditionMeta().label`, used as
 *   the fallback for types without an inline format
 */
export function conditionText(condition, t, conditionLabel) {
  const c = condition;
  if (!c) return '';
  const ops = { gt: '>', gte: '>=', lt: '<', lte: '<=', eq: '=' };
  if (c.type === 'threshold') return `${ops[c.operator] || c.operator} ${c.value}`;
  if (c.type === 'relative_change') {
    const ref = c.reference && c.reference !== 'previous' ? ` (${c.reference})` : '';
    return `${ops[c.operator] || c.operator} ${c.value}%${ref}`;
  }
  if (c.type === 'negative_quarters') return `>= ${c.value} ${t('alerts.negative_quarters_short')}`;
  if (c.type === 'prediction_confidence') return t('alerts.condition_prediction_confidence');
  if (c.type === 'record') return t('alerts.record_' + (c.direction || 'highest'));
  if (c.type === 'count_in_period') {
    const compOp = ops[c.comparison_operator] || c.comparison_operator;
    return `${t('alerts.condition_count_in_period_short')}: ${compOp} ${
      c.comparison_value
    } (${c.period || ''})`;
  }
  if (c.type === 'spread')
    return `${t('alerts.condition_spread_short')}: ${ops[c.operator] || c.operator} ${
      c.value
    } (${c.period || ''})`;
  if (c.type === 'moving_average')
    return `${t('alerts.condition_ma_short')}: ${ops[c.operator] || c.operator} ${
      c.value
    }% (${c.period || ''})`;
  if (c.type === 'trend')
    return `${t('alerts.condition_trend_short')}: ${c.value}x ${c.direction || ''}`;
  if (c.type === 'crosses_ma')
    return `${t('alerts.condition_crosses_ma_short')}: ${c.direction || 'above'} (${c.period ||
      ''})`;
  if (c.type === 'forecast_deviation') {
    const suffix = c.deviation_type === 'absolute' ? '' : '%';
    return `${t('alerts.condition_forecast_short')}: ${ops[c.operator] || c.operator} ${
      c.value
    }${suffix}`;
  }
  if (c.type === 'threshold_duration') {
    const compOp = ops[c.comparison_operator] || c.comparison_operator;
    return `${t('alerts.condition_duration_short')}: ${compOp} ${c.comparison_value} >= ${
      c.value
    }h`;
  }
  if (c.type === 'compound') {
    const logic = (c.logic || 'and').toUpperCase();
    const count = (c.sub_conditions || []).length;
    return `${t('alerts.condition_compound_short')}: ${count} (${logic})`;
  }
  // Fallback: use translated condition label (covers cross_market_spread,
  // state_change, data_missing, and any future types without inline formatting).
  return conditionLabel(c.type);
}

/**
 * The condition as shown on an alert card or row: the backend's plain-language
 * `condition_sentence` (already in the user's language) when it sends one, the
 * locally built `conditionText` otherwise — an older backend has no such field,
 * and a blank or non-string value counts as absent.
 */
export function alertConditionText(alert, t, conditionLabel) {
  const sentence = typeof alert?.condition_sentence === 'string' ? alert.condition_sentence.trim() : '';
  return sentence || conditionText(alert?.condition, t, conditionLabel);
}

/**
 * Which health label an alert's state calls for: its stored `state.health`, or
 * `unevaluable` for an unhealthy alert the worker flagged because its condition
 * could not be judged several times in a row (PI-641). That flag means the data
 * is there, so "No data" would be wrong. Any other cause, or a backend without
 * `unhealthy_cause`, keeps the stored value.
 *
 * @param {object} state  the alert's `state` object
 * @returns {string|undefined}
 */
export function alertHealthKey(state) {
  if (state?.health === 'unhealthy' && state?.unhealthy_cause === 'unevaluable') return 'unevaluable';
  return state?.health;
}
